import { useEffect, useRef, useState } from 'react';
import { PushNotificationManager } from '../utils/push';

/**
 * Hook pour gérer la souscription automatique aux notifications push
 * @param {Object} user - L'utilisateur connecté (null si non connecté)
 * @param {Object} options - Options de configuration
 * @param {boolean} options.enabled - Activer/désactiver l'auto-souscription
 * @param {boolean} options.unsubscribeOnLogout - Se désabonner lors de la déconnexion
 * @param {boolean} options.requestPermissionOnFirstLogin - Demander la permission lors de la première connexion
 * @param {boolean} options.showModalOnFirstLogin - Afficher la modale lors de la première connexion
 * @param {boolean} options.silentSubscribeIfGranted - Souscrire silencieusement si la permission est déjà accordée
 * @param {boolean} options.retryOnFailure - Réessayer en cas d'échec
 * @param {number} options.retryDelay - Délai avant de réessayer (en ms)
 * @param {boolean} options.debug - Activer les logs de debug
 * @returns {Object} Le gestionnaire de notifications push et les états de la modale
 */
export const usePushAutoSubscribe = (user, options = {}) => {
  const pushManagerRef = useRef(null);
  const hasAttemptedRef = useRef(false);
  const retryTimeoutRef = useRef(null);

  // États pour la modale
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [permissionLoading, setPermissionLoading] = useState(false);

  const {
    enabled = true,
    unsubscribeOnLogout = false,
    requestPermissionOnFirstLogin = true,
    showModalOnFirstLogin = true,
    silentSubscribeIfGranted = true,
    retryOnFailure = true,
    retryDelay = 5000,
    debug = false
  } = options;

  const log = (...args) => {
    if (debug) {
      console.log('[PushAutoSubscribe]', ...args);
    }
  };

  // Fonction pour gérer l'acceptation de la permission
  const handlePermissionAccept = async () => {
    if (!pushManagerRef.current) return;

    try {
      setPermissionLoading(true);
      log('User accepted permission request');

      const pushManager = pushManagerRef.current;
      const granted = await pushManager.requestPermission();

      if (granted) {
        await pushManager.subscribe();
        log('Successfully subscribed after permission grant');
        setShowPermissionModal(false);

        // Marquer comme tenté pour éviter de redemander
        hasAttemptedRef.current = true;

        // Sauvegarder dans localStorage que l'utilisateur a déjà vu la modale
        localStorage.setItem('pushNotificationModalShown', 'true');
      } else {
        log('Permission denied by user');
        setShowPermissionModal(false);
      }
    } catch (error) {
      log('Permission request failed:', error);
      if (retryOnFailure) {
        log(`Retrying in ${retryDelay}ms...`);
        retryTimeoutRef.current = setTimeout(() => {
          handlePermissionAccept();
        }, retryDelay);
      }
    } finally {
      setPermissionLoading(false);
    }
  };

  // Fonction pour gérer le refus de la permission
  const handlePermissionDecline = () => {
    log('User declined permission request');
    setShowPermissionModal(false);
    hasAttemptedRef.current = true;

    // Sauvegarder que l'utilisateur a refusé (pour ne pas redemander)
    localStorage.setItem('pushNotificationModalShown', 'true');
    localStorage.setItem('pushNotificationDeclined', 'true');
  };

  // Fonction pour nettoyer les timeouts
  const cleanup = () => {
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
  };

  // Effet principal pour la souscription automatique
  useEffect(() => {
    if (!enabled || !user) return;

    // Éviter les tentatives multiples pour le même utilisateur
    if (hasAttemptedRef.current) return;
    hasAttemptedRef.current = true;

    const autoSubscribe = async () => {
      // Vérifier le support des notifications push
      if (!PushNotificationManager.isSupported()) {
        if (debug) console.log('[PushAutoSubscribe] Push notifications not supported, skipping auto-subscribe');
        return;
      }

      try {
        // Initialiser le gestionnaire de push s'il n'existe pas
        if (!pushManagerRef.current) {
          pushManagerRef.current = new PushNotificationManager({
            onStateChange: (state, data) => {
              log('State change:', state.id);
              if (state.id === 'ERROR' || state.id === 'UNSUPPORTED') {
                console.warn('[PushAutoSubscribe] Error state:', data);
              }
            },
            onSubscriptionChange: (subscription) => {
              log('Subscription changed:', !!subscription);
            },
            onError: (title, error) => {
              console.warn('[PushAutoSubscribe] Error:', title, error);
            }
          });
        }

        const pushManager = pushManagerRef.current;
        const permission = pushManager.getPermission();

        log('Current permission:', permission);

        // Cas 1: Permission accordée mais pas encore souscrit
        if (permission === 'granted' && silentSubscribeIfGranted && !pushManager.isSubscribedToPush()) {
          log('Permission already granted, subscribing silently...');
          await pushManager.subscribe();
          log('Successfully auto-subscribed');
          hasAttemptedRef.current = true;
        }
        // Cas 2: Première connexion - afficher la modale
        else if (permission === 'default' && showModalOnFirstLogin) {
          // Vérifier si l'utilisateur a déjà vu la modale ou refusé
          const modalShown = localStorage.getItem('pushNotificationModalShown');
          const userDeclined = localStorage.getItem('pushNotificationDeclined');

          if (!modalShown && !userDeclined) {
            log('Showing permission modal to user');
            // Attendre un peu que l'interface soit complètement chargée
            setTimeout(() => {
              setShowPermissionModal(true);
            }, 1000);
          } else {
            log('Permission modal already shown or user declined');
            hasAttemptedRef.current = true;
          }
        }
        // Cas 3: Permission refusée - ne rien faire
        else if (permission === 'denied') {
          log('Permission denied, skipping auto-subscribe');
          hasAttemptedRef.current = true;
        }
        // Cas 4: Déjà souscrit
        else if (pushManager.isSubscribedToPush()) {
          log('User already subscribed');
          hasAttemptedRef.current = true;
        }
        else {
          log('No action needed');
          hasAttemptedRef.current = true;
        }

      } catch (error) {
        log('Auto-subscribe failed:', error);

        // Réessayer si configuré pour le faire
        if (retryOnFailure && retryDelay > 0) {
          log(`Retrying in ${retryDelay}ms...`);
          retryTimeoutRef.current = setTimeout(() => {
            hasAttemptedRef.current = false; // Permettre une nouvelle tentative
            autoSubscribe();
          }, retryDelay);
        }
      }
    };

    // Démarrer la souscription automatique
    autoSubscribe();

    // Cleanup function
    return cleanup;
  }, [user, enabled, showModalOnFirstLogin, silentSubscribeIfGranted, retryOnFailure, retryDelay, debug]);

  // Effet pour gérer la déconnexion
  useEffect(() => {
    if (!user && unsubscribeOnLogout && pushManagerRef.current) {
      log('User logged out, unsubscribing...');

      // Reset pour la prochaine connexion
      hasAttemptedRef.current = false;
      setShowPermissionModal(false); // Fermer la modale si ouverte
      cleanup();

      // Désabonner des notifications push
      pushManagerRef.current.unsubscribe().catch(error => {
        console.warn('[PushAutoSubscribe] Failed to unsubscribe on logout:', error);
      });
    } else if (!user) {
      // Reset même si on ne se désabonne pas
      hasAttemptedRef.current = false;
      setShowPermissionModal(false);
      cleanup();
    }
  }, [user, unsubscribeOnLogout, debug]);

  // Cleanup lors du démontage du composant
  useEffect(() => {
    return cleanup;
  }, []);

  return {
    pushManager: pushManagerRef.current,
    isSupported: PushNotificationManager.isSupported(),
    hasAttempted: hasAttemptedRef.current,
    showPermissionModal,
    permissionLoading,
    handlePermissionAccept,
    handlePermissionDecline
  };
};
